import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
} from '@mui/material'
import { H4 } from '../../../../../layouts/TextStyles'
import { capitalize } from '../../../../../utils/functions'
import { courseApiSlice } from '../../../../../features/course/courseSlice'
import { CoursePurchaseState, CourseState } from '../../../../../features/types'
import DiscountTimer from './DiscountTimer'
import { discountBox, flexCenter, flexColumn, logo, price } from '../style'

interface Props {
    coursePurchase: CoursePurchaseState
    endTime: Date
    open: boolean
    getCourseDuration: () => string
    discountPrice: number
    handleClose: () => void
}

const CourseDetailsDialog: React.FC<Props> = ({
    coursePurchase,
    getCourseDuration,
    discountPrice,
    endTime,
    open,
    handleClose,
}) => {
    const styles = {
        discountPrice: {
            color: '#6D6D6D',
            fontSize: '13px',
            textDecoration: coursePurchase.discount > 0 && 'line-through',
            mr: '4px',
        },
    }

    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const { data: course = {} as CourseState, isFetching } =
        courseApiSlice.useGetCourseByIdQuery(coursePurchase.courseId, {
            skip: !open,
        })

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth={true}
        >
            <DialogTitle>
                {coursePurchase[`title${capitalize(locale)}`]}
            </DialogTitle>

            <DialogContent
                sx={{
                    '@media screen and (max-width: 500px)': {
                        padding: 2,
                    },
                }}
            >
                {isFetching && <LinearProgress />}

                <Box sx={discountBox}>
                    <Box sx={flexCenter}>
                        <Box mr={2}>
                            <img
                                src={course.imageKz}
                                alt="course-logo"
                                style={logo as React.CSSProperties}
                            />
                        </Box>

                        <Box
                            mr={2}
                            fontWeight={500}
                        >
                            {coursePurchase[`slogan${capitalize(locale)}`]}
                        </Box>
                    </Box>

                    <Box sx={flexCenter}>
                        <Box
                            mr={2}
                            mb={2}
                        >
                            <Box sx={price}>
                                {formatMessage({ id: 'price' })}
                            </Box>

                            <Box
                                sx={flexColumn}
                                fontWeight={600}
                            >
                                {coursePurchase.discount > 0 && (
                                    <Box sx={styles.discountPrice}>
                                        {coursePurchase.price}т
                                    </Box>
                                )}

                                <Box sx={{ whiteSpace: 'nowrap' }}>
                                    {discountPrice} т / {getCourseDuration()}
                                </Box>
                            </Box>
                        </Box>

                        <DiscountTimer
                            endTime={endTime}
                            discount={coursePurchase.discount}
                        />
                    </Box>
                </Box>
                <Box>
                    <H4>{formatMessage({ id: 'trainingProgram' })}</H4>

                    <Box mt={1}>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: course[
                                    `description${capitalize(locale)}`
                                ],
                            }}
                        ></p>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ m: 2 }}>
                <Button
                    variant={'contained'}
                    onClick={() =>
                        navigate(
                            `checkout/${coursePurchase.courseId}/${coursePurchase.id}`,
                        )
                    }
                >
                    {formatMessage({ id: 'enrollToCourse' })}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CourseDetailsDialog
